import { Experiment, ExperimentClient, ExperimentUser, type Variant } from '@amplitude/experiment-js-client';

import { Device } from '@/store/device/types';
import { User } from '@/store/user/types';

export const isEnrolledVariant = (variant: Pick<Variant, 'value'> | undefined): boolean =>
	variant?.value === 'treatment' || variant?.value === 'on';

export const AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE = 'client-unavailable';

export type AmplitudeExperimentClient = ExperimentClient | typeof AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE;

export const initAmplitudeExperimentClient = async (
	amplitudeExperimentsDeploymentKey = process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_DEPLOYMENT_KEY,
): Promise<AmplitudeExperimentClient> => {
	if (!amplitudeExperimentsDeploymentKey) {
		throw Error('Missing amplitude experiments deployment key');
	}

	try {
		// `pollOnStart: false` disables the SDK's 60s flag-config poller
		// (`flag.lab.amplitude.com`). That poller exists for *local*-evaluation
		// flags; all of our experiments are remote-evaluated, so it fetches data
		// we never consume. Worse, the poller fires the request via setInterval
		// with no error handling, so each 10s timeout leaks an unhandled promise
		// rejection ("Request timeout after 10000 milliseconds") into RUM. The
		// initial remote variant fetch below (`start()`) is unaffected.
		const experimentClient = Experiment.initializeWithAmplitudeAnalytics(amplitudeExperimentsDeploymentKey, {
			pollOnStart: false,
		});
		await experimentClient.start();

		return experimentClient;
	} catch (err) {
		return AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE;
	}
};

export const setupAmplitudeExperimentUser = (user: User | null, device: Device): ExperimentUser => {
	return {
		device_id: device.deviceId,
		user_id: user?.id,
		platform: 'web',
		language: device.language,
		user_properties: {
			...(user?.created_at && { user_account_created: new Date(user.created_at).toISOString() }),
			...(user?.subscription && {
				subscription_in_free_trial_window: user.subscription.in_free_trial_window,
			}),
			...(user?.subscription && { subscription_is_valid: user.subscription.valid }),
			...(user?.subscription && { subscription_type: user.subscription.type }),
		},
	};
};

export enum AmplitudeExperimentNames {
	FAMILY_PLAN_TOGGLE = 'www-family-plan-toggle',
	GOAL_QUESTIONNAIRE_POST_UPSELL = 'www-goal-questionnaire-post-upsell',
	RUNWAYER_QUIZ_ON_CALM_HOME_PAGE = 'www-runwayer-quiz-on-calm-home-page',
	REFUND_SAVE_OFFER = 'www-refund-save-offer',
	CHECKOUT_SESSIONS = 'www-checkout-sessions',
	// Treatment lets existing users sign in on the Runwayer redemption page; control
	// keeps it signup-only. Evaluated client-side so exposure fires only for visitors
	// who actually see the redemption form.
	RUNWAYER_SIGNIN = 'www-runwayer-signin',
	// Kill-switch for routing SSR data fetches to internal app-api. Default OFF.
	SSR_APP_API_DIRECT = 'www-ssr-app-api-direct',
	TRUEMED_PAYMENT = 'truemed-payment',
}
